import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface VendorRanking {
  rank: number;
  vendorId: string;
  vendorName: string | null;
  adCount: number;
  distinctTerritoryCount: number;
  distinctDistrictCount: number;
  distinctMediumCount: number;
  performanceIndexPct: number;
  lastActivityAt: string | null;
}

/** §15/§18 vendor-wise geo performance analytics. */
export function VendorAnalyticsScreen(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<VendorRanking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: VendorRanking[] }>('/v1/geo-intel/vendors/rankings', { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.vendor.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="vendor-analytics">
      <h1>{t('m16report.vendor.title')}</h1>
      {error && <p role="alert" aria-label="vendor-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <DataTable
          ariaLabel="vendor-table"
          emptyMessage={t('m16report.vendor.empty')}
          rowKey={(v) => v.vendorId}
          rows={rows}
          columns={[
            { key: 'rank', header: '#', render: (v) => v.rank },
            { key: 'vendor', header: t('m16report.col.vendor'), render: (v) => v.vendorName ?? '—' },
            { key: 'ads', header: t('m16report.col.ads'), render: (v) => v.adCount },
            { key: 'territories', header: t('m16report.col.territories'), render: (v) => v.distinctTerritoryCount },
            { key: 'districts', header: t('m16report.col.districts'), render: (v) => v.distinctDistrictCount },
            { key: 'mediums', header: t('m16report.col.mediums'), render: (v) => v.distinctMediumCount },
            { key: 'perf', header: t('m16report.vendor.col.perf'), render: (v) => <span aria-label={`perf-${v.vendorId}`}>{number(v.performanceIndexPct)}</span> },
          ]}
        />
      )}
    </main>
  );
}
